/**
 * Utility functions to manage Calls to oxygen
 * See dpe/dhx-auth repo, for usage details
 */
import {v1 as uuidv1} from 'uuid';
import {
  toggleLoginHappening,
  updateUserData,
  updateOpenAferRedirect,
  updatePopUpMainWindow,
  updateSessionTokenAvailable,
  updateExportControlBlock,
  updateBlockedCSN,
  updateBlockedAccountName,
  updateAccountCSNs,
} from '../actions';
import {store} from '../configureStore';
import {
  updateLdClient,
  shouldLdClientBeUpdated,
  isStorageAccessible,
  parseQueryParams,
  shouldUseAuthFromHostPage,
} from '../utils';
import log from 'loglevel';
import {AuthClient} from '@adsk/auth-pkce-sdk';
import qs from 'qs';
import {PATClientID, PATClientSecret, getPATbyEntitlement} from '../app.config';
import {getOxygenReturnUrl} from './utilFunctions';
import {acadInterop} from './utilAcadInterop';
import worker from 'workerize-loader!../workers/worker'; // eslint-disable-line import/no-webpack-loader-syntax
import {getJwtEntitlements} from '@adsk/ha-entitlements-util';
import isEmpty from 'lodash/isEmpty';
import jwtDecode from 'jwt-decode';
import {dtReportCustomError} from '@adsk/ha-dynatrace-util';
import {BackendConnectorUtil} from '@adsk/ha-backend-connector-util';

let workerInstance = worker(); // `new` is optional

window.ua = window.ua || {auth: {}, acad_interop: {}};

const parentWebsite = window.location.origin;
const parentPathname = window.location.pathname;

/*
 * Only used in ACDMOBILE flow.
 * id token exp property is called 'expires_in'
 */
const isAccessTokenValid = (jwt) => {
  if (!jwt) return false;
  try {
    // log.debug('[Auth] Decoded JWT', jwtDecode(jwt));
    const {exp} = jwtDecode(jwt);

    // Reject Expired JWTs
    if (exp * 1000 < Date.now()) throw new Error('Token Expired');

    return true;
  } catch (error) {
    log.error(`[Auth] ${error}`);
    return false;
  }
};

// Set window.ua.auth.getAccessToken based on Context
// check for in-product get token functions
// if none, setup Forge client and getTokenSilently()
const setupGlobalAccessTokenDelegate = async ({
  tier,
  product,
  isInProduct,
  authParams = {},
  parentDomain,
  utmSource,
}) => {
  const forgeConfig = getForgeConfig(tier);
  let inProductAuthNotConfigured = false;

  const {sessionTokenAvailable, useCFPAuth} = store.getState()?.appSettings;

  if (shouldUseAuthFromHostPage({parentDomain, utmSource})) {
    console.log('[AUTH] - fetching token from session storage');
    if (!sessionTokenAvailable) {
      forgeRemoveUserDetailsFromSessionStorage();
    }
    window.ua.auth.getAccessToken = () => {
      return {
        access_token: sessionTokenAvailable,
      };
    };
  } else if (authParams.authType === 'PAT') {
    log.warn('[Auth] Using PAT Test Credentials');
    const PAT = getPATbyEntitlement(authParams.entitlement);
    window.ua.auth.getAccessToken = () =>
      new Promise((resolve) => {
        forgeGetAccessTokenWithPAT(PAT, PATClientID, PATClientSecret, tier)
          .then((token) => {
            resolve({access_token: token});
          })
          .catch((error) => {
            console.error(
              'Failed to setuo access token delegate for PATs',
              error
            );
          });
      });
  } else if (useCFPAuth) {
    log.warn('[Auth] Using CFP auth provider');
    // Sets up a function to get an access token from the parent page CFP Provider using postMessage.
    // Resolves with the token if a response is received within 5 seconds, otherwise rejects with an error.
    try {
      window.ua.auth.getAccessToken = () =>
        CFPAuthproviderDelegate({
          CFPAuthMethod: 'getIdToken',
        });
    } catch (error) {
      log.error('Failed to setup access token delegate for CFP', error);
      forgeRemoveUserDetailsFromSessionStorage();
    }
  } else if (isInProduct) {
    log.debug(`[Auth] Context is In-Product - ${product}`);
    switch (product) {
      case 'NINVFUS':
        if (window.neutronJavaScriptObject) {
          try {
            // Setup global auth token delegate for Fusion
            window.ua.auth.getAccessToken = () =>
              new Promise((resolve) => {
                window.neutronJavaScriptObject.executeQuery(
                  'getOauth2TokenForUHM',
                  '',
                  (token) => {
                    resolve({access_token: token});
                  }
                );
              });
          } catch (error) {
            log.error(
              'Failed to setup access token delegate for Fusion360',
              error
            );
            forgeRemoveUserDetailsFromSessionStorage();
          }
        } else {
          inProductAuthNotConfigured = true;
          log.warn(
            `[Auth] ${product} In-Product getToken helper function is not defined. Fallback to create new Auth client`
          );
        }
        break;
      case 'ACD':
        if (
          typeof window.execAsync === 'function' &&
          typeof window.apiVersion === 'function' &&
          window.apiVersion() >= 3
        ) {
          window.ua.acad_interop.getContextInfo = (count) => {
            return acadInterop.invokeAcadInteropFunction({
              functionName: 'help_Api.getContextInfo',
              invokeAsCommand: false,
              functionParams: {count},
            });
          };

          window.ua.auth.getAccessToken = async () => {
            const token = acadInterop.invokeAcadInteropFunction({
              functionName: 'Ac_ApplicationInterop.getSignInToken',
              functionParams: {},
            });

            return {
              access_token: await token,
            };
          };
          break;
        } else {
          inProductAuthNotConfigured = true;
          log.warn(
            `[Auth] ${product} getToken helper function is not defined. Fallback to create new Auth client`
          );
        }
        break;
      case 'ACADMOBILE':
        if (isAccessTokenValid(authParams.accessToken)) {
          log.info('[Auth] Using accessToken query param Credentials');
          // replace access token delegate with token parameter value
          window.ua.auth.getAccessToken = () => ({
            access_token: authParams.accessToken,
          });
        }
        break;
      default:
        inProductAuthNotConfigured = true;
        break;
    }
    if (inProductAuthNotConfigured) {
      window.ua.auth.client = window.ua?.auth?.client
        ? window.ua.auth.client
        : new AuthClient({
            env: forgeConfig.env, // 'dev', 'stg', 'prod'
            client_id: forgeConfig.client_id,
            redirect_uri: forgeConfig.redirect_uri,
            useRefreshTokens: true,
            redirect_uri_iframe: forgeConfig.redirect_uri_iframe,
            scope: 'data:read openid user-profile:read',
          });

      // Setup global AccessToken delegate
      window.ua.auth.getAccessToken = () =>
        window.ua.auth.client.getTokenSilently({
          ignoreCache: true,
          detailedResponse: true,
        });
    }
  } else {
    log.debug('[Auth] Using UA Client Credentials');
    window.ua.auth.client = window.ua?.auth?.client
      ? window.ua.auth.client
      : new AuthClient({
          env: forgeConfig.env, // 'dev', 'stg', 'prod'
          client_id: forgeConfig.client_id,
          redirect_uri: forgeConfig.redirect_uri,
          useRefreshTokens: true,
          redirect_uri_iframe: forgeConfig.redirect_uri_iframe,
          scope: 'data:read openid user-profile:read',
        });

    // Setup global AccessToken delegate
    window.ua.auth.getAccessToken = () =>
      window.ua.auth.client.getTokenSilently({
        ignoreCache: true,
        detailedResponse: true,
      });
  }
};

// get user data from forge using access token
const forgeGetUserData = async (tier) => {
  try {
    const {token, isIdToken} = await forgeGetToken();
    if (typeof token === 'string' && token !== '') {
      return await forgeGetUser(token, isIdToken, tier);
    } else {
      log.error('Empty or invalid token', token);
      forgeRemoveUserDetailsFromSessionStorage();
    }
  } catch (e) {
    log.error('Error fetching userData: ', e);
  }
};
/*
 * Get Analytics ID used in PAT & In-product auth flows
 * *No longer used in Open Id Connect flow*
 */
const forgeGetAnalyticsID = async (tier) => {
  const {analyticsIdEndpoint} = getForgeConfig(tier);
  const {access_token: token} = await window.ua.auth.getAccessToken();
  if (typeof token === 'string' && token !== '') {
    const config = {
      headers: {Authorization: `Bearer ${token}`},
    };
    try {
      const data = await workerInstance.getApiData({
        url: analyticsIdEndpoint,
        options: config,
      });

      log.debug('[Auth] - analytics id: ', data);
      return data?.analyticsId;
    } catch (e) {
      log.error('Error fetching userData: ', e, token);
    }
  } else {
    log.error('Empty or invalid token', token);
  }
};

// Fetch user entitlements from Beehive.
// Both UserId and JWT are required because the entitlements service makes calls to other systems.
// This means it needs the userID decoded from the JWT and the userID received from /userData to match independently
const forgeGetUserEntitlements = async (userId, forgeSetUpOptions) => {
  let token;
  const {tier, isInProduct, product, authParams} = forgeSetUpOptions;
  const {userEntitlementsEndpoint} = getForgeConfig(tier);
  const {id_token, access_token} = await window.ua.auth.getAccessToken();

  if (typeof id_token === 'string' && id_token !== '') {
    token = id_token;
  } else {
    token = access_token;
  }

  // use any support subscription claims as overrides
  const claimedEntitlements = getJwtEntitlements(token);
  if (!isEmpty(claimedEntitlements)) return claimedEntitlements;

  if (typeof token === 'string' && token !== '') {
    const config = {
      headers: {
        Authorization: token,
        'x-user-id': userId,
      },
    };
    try {
      const includeEduVerifyStatus =
        isInProduct && product === 'ACD' ? false : true;
      const checkUsageType = isInProduct && product === 'ACD' ? false : true;
      const data = await workerInstance.getApiData({
        url:
          userEntitlementsEndpoint +
          `?includeEduVerifyStatus=${includeEduVerifyStatus}&includeEpsStatus=true&checkUsageType=${checkUsageType}`,
        options: config,
      });
      log.debug('[Auth] - userEntitlements: ', data);
      // Hard coded condition for ACAD mobile to override any response lower than Standard
      // from Beehive entitlement api
      // TODO: Talk to ACAD mobile team and ask them to pass support level as claims as its more secure
      if (
        isInProduct &&
        product === 'ACADMOBILE' &&
        !isEmpty(authParams?.accessToken) &&
        ['silver', 'none', 'gold', undefined].includes(
          data?.highestLevelBenefitFeature?.toLowerCase()
        )
      ) {
        return {
          highestLevelFeatureId: 'PhoneSupport',
          phoneSupport: true,
          productFeatureIds: [],
          epSupport: false,
          premiumAdmin: false,
          highestLevelBenefitFeature: 'Gold_Subscriber',
        };
      } else {
        return data;
      }
    } catch (e) {
      log.error('Error fetching entitlements: ', e, token);
    }
  } else {
    log.error('Empty or invalid token', token);
    forgeRemoveUserDetailsFromSessionStorage();
  }
};

const forgeSaveUserDetailsToSessionStorage = async ({
  userData,
  userEntitlements,
  analyticsId,
  isExportControlBlock
}
) => {
  try {
    const currentUserEntitlements = {
      ...store.getState().userData.userEntitlements,
    };

    let token;
    const {id_token, access_token} = await window.ua.auth.getAccessToken();

    if (typeof id_token === 'string' && id_token !== '') {
      token = id_token;
    } else {
      token = access_token;
    }

    const queryParams = window.location.search;
    const {mcvisid = null} = parseQueryParams(queryParams, ['mcvisid']);

    let tmp = userData
      ? {
          userid: userData.userId,
          fullname: `${userData.firstName} ${userData.lastName}`,
          first: userData.firstName,
          last: userData.lastName,
          email: userData.emailId,
          authorization: token,
          // DO NOT SEND with JWT.
          // requestId: params.requestId || null,
        }
      : {};

    // update Adobe Analytics User.uhm_ui value
    updateAdobeAnalyticsUserUi(analyticsId);

    let userStorePayload = {
      userDetails: tmp,
      userEntitlements: {...userEntitlements},
      analyticsId: analyticsId,
      loginHappening: false,
      mcvisid: mcvisid,
      isExportControlBlock
    };

    const response = store.dispatch(updateUserData(userStorePayload));
    if (
      response &&
      shouldLdClientBeUpdated(currentUserEntitlements, userEntitlements)
    ) {
      // Adds/Updates entitlementLevel for LD userObjects
      updateLdClient();
    }

    // store.dispatch(toggleLoginHappening(false));
  } catch (e) {
    log.error('unable to forgeSaveUserDetailsToSessionStorage', e);
    store.dispatch(toggleLoginHappening(false));
  }
};

// delete logged in params from sessionStorage
const forgeRemoveUserDetailsFromSessionStorage = () => {
  try {
    const isLocalStorageAvailable = isStorageAccessible(() => localStorage);
    let getStore = store.getState();
    let getCurrentUser = getStore.userData.userDetails;
    let getCurrentUserAnalyticsId = getStore.userData.analyticsId;
    let getCurrentEteSessionSign = getStore.userData.eteSessionSign;
    let tmpAnalyticsId;
    isLocalStorageAvailable && localStorage.removeItem('uaOxygenId');

    if (getCurrentUserAnalyticsId) {
      tmpAnalyticsId = getCurrentUserAnalyticsId;
    } else if (
      isLocalStorageAvailable &&
      localStorage.getItem('akp-analytics-userid')
    ) {
      tmpAnalyticsId = localStorage
        .getItem('akp-analytics-userid')
        .replace(/^"|"$/g, '');
    } else {
      tmpAnalyticsId = uuidv1();
    }
    store.dispatch(updateSessionTokenAvailable('')); // Remove the jwt token from the store.
    let userStorePayload = {
      userDetails: {},
      userEntitlements: {},
      analyticsId: tmpAnalyticsId,
      eteSessionSign: getCurrentEteSessionSign,
      transactionSessionId: null,
      loginHappening: false,
      profile: {},
    };

    // update Adobe Analytics User.uhm_ui value
    updateAdobeAnalyticsUserUi(userStorePayload.analyticsId);

    if (
      (getCurrentUser && getCurrentUser.userid) ||
      !getCurrentUserAnalyticsId
    ) {
      const resp = store.dispatch(updateUserData(userStorePayload));
      if (resp) {
        // Removes entitlementLevel from LD userObjects
        updateLdClient();
      }
    } else {
      store.dispatch(toggleLoginHappening(false));
    }
  } catch (e) {
    log.error('unable to oxygenRemoveUserDetailsFromSessionStorage', e);
    store.dispatch(toggleLoginHappening(false));
  }
};

// set parent Adobe Analytic sui value
const updateAdobeAnalyticsUserUi = (val) => {
  let getStore = store.getState();
  let getPageDomain = getStore.appSettings.pageDomain;
  const target = getPageDomain;

  if (window.top !== window) {
    window.parent.postMessage({type: 'UHM_SET_AA_UI', uiVal: val}, target);
  } else {
    window.digitalData = window.digitalData || {};
    window.digitalData.user = window.digitalData.user || {};
    window.digitalData.user.uhm_ui = val;
  }
};

const getForgeConfig = (tier = 'prd') => {
  if (tier === 'prd') {
    return {
      domain: 'https://developer.api.autodesk.com/authentication/v2',
      env: 'prod',
      client_id: '7LJG9Rv9FLuxuVe7MBAIxW8zmYPTHt0d',
      analyticsIdEndpoint:
        'https://developer.api.autodesk.com/identity/v1/users/@me/analytics',
      redirect_uri: `${parentWebsite}${parentPathname}?redirectOnly=true`,
      PAT_endpoint:
        'https://developer.api.autodesk.com/authentication/v2/token',
      userInfoEnpoint: 'https://api.userprofile.autodesk.com/userinfo',
      userEntitlementsEndpoint:
        'https://akn.services.autodesk.com/kpentitlementservices/user/entitlement/v4/app/knowledge/entitlementdata',
      redirect_uri_iframe: `${getRedirectUriIframe()}/auth_callback.html`,
    };
  } else {
    return {
      domain: 'https://developer-stg.api.autodesk.com/authentication/v2',
      env: 'stg',
      client_id: 'dhbwAGGEM9K655euvO84QdZtWHSA5Ghg',
      analyticsIdEndpoint:
        'https://developer-stg.api.autodesk.com/identity/v1/users/@me/analytics',
      redirect_uri: `${parentWebsite}${parentPathname}?redirectOnly=true`,
      PAT_endpoint:
        'https://developer-stg.api.autodesk.com/authentication/v2/token',
      userInfoEnpoint: 'https://api.userprofile-stg.autodesk.com/userinfo',
      userEntitlementsEndpoint:
        'https://akn.services-staging.autodesk.com/kpentitlementservices/user/entitlement/v4/app/knowledge/entitlementdata',
      redirect_uri_iframe: `${getRedirectUriIframe()}/auth_callback.html`,
    };
  }
};

const getRedirectUriIframe = () => {
  let redirectUrlIframe = `${window.location.origin}${window.location.pathname}`;
  const substringToRemove = '/index.html';
  if (redirectUrlIframe.includes(substringToRemove)) {
    redirectUrlIframe = redirectUrlIframe.slice(0, -substringToRemove.length);
  }
  return redirectUrlIframe;
};

// default to prd
const forgeSignIntoAnotherAccount = async (tier = 'prd') => {
  forgeRemoveUserDetailsFromSessionStorage();
  store.dispatch(updateOpenAferRedirect(true));
  const {pageDomain: parentDomain, utmSource, useCFPAuth} = store.getState()?.appSettings;
  if (shouldUseAuthFromHostPage({parentDomain, utmSource})) {
    window.parent.postMessage({type: 'ME_MENU_SIGN_IN'}, parentDomain);
  } else if (useCFPAuth) {
      try {
        forgeRemoveUserDetailsFromSessionStorage();
        CFPAuthproviderDelegate({
          CFPAuthMethod: 'signIntoAnother',
        });
      } catch (e) {
        console.error('[CFP] error loggin in or out');
      }
  } else {
    const parentUrl = getOxygenReturnUrl();
    const redirectUrl = getForgeConfig(tier).redirect_uri;
    log.debug('parent url - redirect url', parentUrl);
    const isSessionStorageAvailable = isStorageAccessible(() => sessionStorage);
    isSessionStorageAvailable &&
      sessionStorage.setItem('parentReturnUrl', parentUrl);
    const isLocalStorageAvailable = isStorageAccessible(() => localStorage);
    isLocalStorageAvailable &&
      localStorage.setItem('parentReturnUrl', parentUrl);
    await window.ua.auth.client.loginWithRedirect({
      appState: {
        returnTo: parentUrl,
      },
      prompt: 'login',
      redirect_uri: redirectUrl,
      redirectTarget: window.parent.location,
      redirectMethod: 'replace',
    });
  }
};

const forgeGetAccessTokenWithPAT = async (
  clientToken,
  clientId,
  clientSecret,
  tier
) => {
  const getExchangeAuthHeader = (clientId, clientSecret) => {
    const credentials = `${clientId}:${clientSecret}`;
    const clientAuth = Buffer.from(credentials).toString('base64');
    return `Basic ${clientAuth}`;
  };

  const authEndpoint =
    tier === 'prd'
      ? 'https://developer.api.autodesk.com'
      : 'https://developer-stg.api.autodesk.com';

  // payload must be url encoded
  const authParams = qs.stringify({
    grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
    scope: 'user:read',
    subject_token_type:
      'urn:adsk:params:oauth:token-type:personal_access_token',
    subject_token: clientToken,
  });
  const config = {
    method: 'POST',
    headers: {
      Authorization: getExchangeAuthHeader(clientId, clientSecret),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: authParams,
  };

  try {
    const data = await workerInstance.getApiData({
      url: authEndpoint + '/authentication/v2/token',
      options: config,
    });

    return data.access_token;
  } catch (error) {
    return Promise.reject(error);
  }
};

const callUserInfoEndpoint = async (token, tier) => {
  try {
    const {userInfoEnpoint} = getForgeConfig(tier);
    const config = {
      headers: {Authorization: `Bearer ${token}`},
    };

    const data = await workerInstance.getApiData({
      url: userInfoEnpoint,
      options: config,
    });

    const analyticsId = await forgeGetAnalyticsID(tier);

    return {
      userData: buildUserData(data),
      userAnalyticsID: analyticsId,
    };
  } catch (err) {
    log.error(`[Auth][PAT][UserInfo]`, err);
  }
};

const forgeHandleRedirectCallback = async (props) => {
  const isSessionStorageAvailable = isStorageAccessible(() => sessionStorage);
  const isLocalStorageAvailable = isStorageAccessible(() => localStorage);

  const parentReturnUrlSessionStorage =
    isSessionStorageAvailable && sessionStorage.getItem('parentReturnUrl');

  const parentReturnUrlLocalStorage =
    isLocalStorageAvailable && localStorage.getItem('parentReturnUrl');

  if (!isEmpty(parentReturnUrlSessionStorage)) {
    window.top.location = parentReturnUrlSessionStorage;
  } else if (!isEmpty(parentReturnUrlLocalStorage)) {
    window.top.location = parentReturnUrlLocalStorage;
  } else {
    window.top.location = 'https://www.autodesk.com/support/contact-support';
  }
};

const checkExportControlStatus = async (userData, token, tier) => {
  let isExportControlBlock = false;

  const ecPayload = {
    oxygenId: userData.userId,
  };

  try {
    const ecStatus = await BackendConnectorUtil({
      type: 'getExportControlStatus',
      payload: ecPayload,
      tier,
      accessToken: token.access_token,
    });

    if (ecStatus && ecStatus.status === 200) {
      const status = ecStatus?.data?.getExportControlStatus?.ecStatus;
      if (status && ['BLOCK', 'REVIEW'].includes(status)) {
        isExportControlBlock = true;
        store.dispatch(updateExportControlBlock(isExportControlBlock));
        return;
      }
    } else {
      const userAccountsResponse = await BackendConnectorUtil({
        type: 'getUserAccounts',
        payload: {
          params: {
            oxygenId: userData.userId,
            emailId: userData.emailId,
          },
        },
        tier,
        accessToken: token.access_token,
      });

      if (userAccountsResponse?.errors) {
        console.error(
          'Error from User Accounts API:',
          userAccountsResponse.errors,
        );
        store.dispatch(updateExportControlBlock(isExportControlBlock));
        return;
      }

      const userAccounts = userAccountsResponse?.data?.getUserAccounts || [];

      if (userAccounts.length === 0) {
        console.log('No accounts found for user.');
        store.dispatch(updateExportControlBlock(isExportControlBlock));
        return;
      }
      // Extract accountCSNs from userAccounts and scag field
      let accountCSNs = userAccounts.map((account) => account.accountCSN);
      // Check if any account has scag and extract accountCSNs from scag array
      userAccounts.forEach((account) => {
        if (account.scag && Array.isArray(account.scag)) {
          const scagCSNs = account.scag.map(
            (scagAccount) => scagAccount.accountCSN,
          );
          accountCSNs = [...accountCSNs, ...scagCSNs];
        }
      });
      store.dispatch(updateAccountCSNs(accountCSNs));

      for (const accountCSN of accountCSNs) {
        const ecPayloadForAccount = {
          oxygenId: accountCSN,
        };

        const accountEcStatus = await BackendConnectorUtil({
          type: 'getExportControlStatus',
          payload: ecPayloadForAccount,
          tier,
          accessToken: token.access_token,
        });

        const status = accountEcStatus?.data?.getExportControlStatus?.ecStatus;
        if (status && ['BLOCK', 'REVIEW'].includes(status)) {
          isExportControlBlock = true;
          console.log('Blocking condition met for accountCSN: ', accountCSN);
          const {accountName = ''} =
          userAccounts.find((account) => account.accountCSN === accountCSN) ||
          {};
          store.dispatch(updateExportControlBlock(isExportControlBlock));
          store.dispatch(updateBlockedCSN(accountCSN));
          store.dispatch(updateBlockedAccountName(accountName));
          return;
        }
      }
    }

    store.dispatch(updateExportControlBlock(isExportControlBlock));
    return;
  } catch (error) {
    console.error('Error from Export Control or User Accounts API:', error);
    store.dispatch(updateExportControlBlock(isExportControlBlock));
    return;
  }
};

const forgeSetupAsync = async (forgeSetUpOptions) => {
  try {
    const {authParams = {}, tier} = forgeSetUpOptions;
    store.dispatch(toggleLoginHappening(true));

    // set window.ua.auth.client.getTokenSilently() based on integration type
    // (dotcom, Fusion360, AutoCAD)
    await setupGlobalAccessTokenDelegate(forgeSetUpOptions);

    if (authParams.code && authParams.state) {
      await forgeHandleRedirectCallback(forgeSetUpOptions);
    }

    // // trigger 'login_required' error from forge and handle before trying other calls
    const token = await window.ua.auth.getAccessToken();

    // If using token from session storage, getAccessToken() will not throw an error
    // So we need to see if the token is empty and remove user data if so
    if (!token) {
      forgeRemoveUserDetailsFromSessionStorage();
      return;
    }

    // need to wait for userID for entitlements
    const {userData, userAnalyticsID} = await forgeGetUserData(tier);

    // check if use is the same since reload
    // if so, dont call entitlments API
    const previousUserDetails = store.getState().userData.userDetails;
    const previousUserEntitlements = store.getState().userData.userEntitlements;
    let userEntitlements = {};
    let isExportControlBlock = false;
    if (previousUserDetails.userid === userData.userId) {
      userEntitlements = previousUserEntitlements;
    } else {
      userEntitlements = await forgeGetUserEntitlements(
        userData.userId,
        forgeSetUpOptions
      );
      /**
       * Export control status API
       */
      const isEduUser = userEntitlements.eduVerification && !isEmpty(userEntitlements.eduVerification);
      if (!isEduUser) {
        checkExportControlStatus(userData, token, tier); // No await here
      }
    }

    // wait for analytics ID and entitlements before updating redux store
    const userDetailsResults = {
      analyticsId: userAnalyticsID,
      userData: userData,
      userEntitlements: userEntitlements,
      isExportControlBlock
    };

    await forgeSaveUserDetailsToSessionStorage(userDetailsResults);
  } catch (e) {
    if (e.message !== 'Login required') {
      console.log('Forge Auth setup error', e);
      dtReportCustomError({
        error: e,
        errorCode: 'FG_USR_ERR',
        errorText: 'Error during forge sign in(legacy)',
      });
    }
    forgeRemoveUserDetailsFromSessionStorage();
  }
};

const buildUserData = (userData) => {
  if (userData?.userid) {
    return {
      userId: userData?.userid,
      firstName: userData?.first_name,
      lastName: userData?.last_name,
      emailId: userData?.user_email,
    };
  } else if (userData?.sub) {
    return {
      userId: userData?.sub,
      firstName: userData?.given_name,
      lastName: userData?.family_name,
      emailId: userData?.email,
    };
  } else {
    log.debug(`[Auth] No user data available:`, userData);
    return {
      userId: '',
      firstName: '',
      lastName: '',
      emailId: '',
    };
  }
};

const forgeGetUser = async (token, isIdToken, tier) => {
  try {
    const data = jwtDecode(token);
    log.debug('[Auth] - userData: ', data);
    const {userid: userId} = data;
    console.log('[Auth] - userData: userId ', userId);
    const isLocalStorageAvailable = isStorageAccessible(() => localStorage);
    isLocalStorageAvailable && localStorage.setItem('uaOxygenId', userId);

    if (isIdToken) {
      return {
        userData: buildUserData(data),
        userAnalyticsID: data?.analytics_id,
      };
    } else {
      return await callUserInfoEndpoint(token, tier);
    }
  } catch (e) {}
};

const forgeGetToken = async () => {
  let isIdToken = false;
  const {id_token, access_token} = await window.ua.auth.getAccessToken();

  if (typeof id_token === 'string' && id_token !== '') {
    isIdToken = true;
    return {token: id_token, isIdToken};
  } else {
    return {token: access_token, isIdToken};
  }
};


const CFPAuthproviderDelegate = ({CFPAuthMethod = 'getIdToken'}) =>
  new Promise((resolve, reject) => {
    log.debug(
      `[Auth][CFPAuthproviderDelegate] CFPAuthMethod: ${CFPAuthMethod}`,
    );
    function handleMessage(event) {
      if (event.data?.type === 'CFP_RESPONSE' && event.data?.response) {
        clearTimeout(timeout);
        window.removeEventListener('message', handleMessage);
        switch (CFPAuthMethod) {
          case 'getIdToken':
            resolve({
              id_token: event.data.response,
              access_token: event.data.response,
            });
            break;
          case 'isLoggedIn':
            resolve({isLoggedIn: event.data.response});
            break;
          case 'getAccessToken':
            resolve({
              id_token: event.data.response,
              access_token: event.data.response,
            });
            break;
          case 'getUserProfile':
            resolve({userProfile: event.data.response});
            break;

          default:
            resolve({
              id_token: event.data.response,
              access_token: event.data.response,
            });
            break;
        }
      }
    }

    const timeout = setTimeout(() => {
      window.removeEventListener('message', handleMessage);
      reject(new Error('Token request timed out'));
    }, 5000); // 5 seconds timeout
    if (!['login', 'logout', 'signIntoAnother'].includes(CFPAuthMethod)) {
      // IF the method is login or logout we dont need to wait for response from the parent window.
      window.addEventListener('message', handleMessage);
    }
    window.parent.postMessage(
      {type: 'CFP_AUTH_PROVIDER', CFPAuthMethod: CFPAuthMethod},
      '*',
    );
  });

export {
  forgeGetUserData,
  forgeGetAnalyticsID,
  forgeSaveUserDetailsToSessionStorage,
  forgeRemoveUserDetailsFromSessionStorage,
  forgeSignIntoAnotherAccount,
  forgeGetAccessTokenWithPAT,
  forgeSetupAsync,
};
